<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="element-island">
          <h1 class="text-3xl font-bold text-gray-900">
            {{ $t('HeadContract.DelaysSchedule.title') }}
          </h1>

          <p class="mt-2 text-sm text-gray-600">
            {{ $t('HeadContract.DelaysSchedule.subTitle') }}
          </p>
        </div>

        <div
          v-if="projectsStore.project?.unarchived && !isReadOnly"
          class="element-island flex items-center"
        >
          <button
            type="button"
            class="btn btn--secondary-blue flex items-center space-x-2"
            @click="showCreateDelayModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>
              {{ $t('HeadContract.DelaysSchedule.button.addDelay') }}
            </span>
          </button>
        </div>
      </div>

      <div class="element-island overflow-y-auto flex flex-col flex-1 p-4">
        <template v-if="delays.length">
          <div
            v-show="!delaysExpanded"
            class="flex space-x-4 mb-4"
          >
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 w-1/3">
              <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-3 rounded-t-xl">
                <div class="flex items-center justify-between">
                  <h2 class="font-semibold text-white">
                    {{ $t('HeadContract.DelaysSchedule.projectSummary') }}
                  </h2>

                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="space-y-4">
                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.contractEndDate') }}</span>

                    <span class="text-sm font-semibold text-gray-900">{{ project.contractStartDate.toLocaleDateString() }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.approvedDelayDays') }}</span>

                    <span class="text-sm font-semibold text-green-600">{{ delaysSummary.approved_delays_total }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.approvedContractEndDate') }}</span>

                    <span class="text-sm font-semibold text-blue-600">{{ project.estimateContractEndDate(delaysSummary.approved_delays_total).toLocaleString() }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.unapprovedDelayDays') }}</span>

                    <span class="text-sm font-semibold text-yellow-600">{{ delaysSummary.unapproved_delays_total }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.anticipatedContractEndDate') }}</span>

                    <span class="text-sm font-semibold text-gray-900">{{ project.estimateContractEndDate(totalDelay).toLocaleString() }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 w-1/3">
              <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-3 rounded-t-xl">
                <div class="flex items-center justify-between">
                  <h2 class="font-semibold text-white">
                    {{ $t('HeadContract.DelaysSchedule.delaySynopsis') }}
                  </h2>

                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-4 h-4 text-white">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="space-y-4">
                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.registered') }}</span>

                    <span class="text-sm font-semibold text-blue-600">{{ delaysSummary.registered }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.submitted') }}</span>

                    <span class="text-sm font-semibold text-purple-600">{{ delaysSummary.submitted }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.assessed') }}</span>

                    <span class="text-sm font-semibold text-orange-600">{{ delaysSummary.provisioned }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.DelaysSchedule.approved') }}</span>

                    <span class="text-sm font-semibold text-green-600">{{ delaysSummary.approved }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex flex-col space-y-4 w-1/3"
            >
              <div class="flex h-full">
                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    {{ $t('HeadContract.DelaysSchedule.delaysByStatus') }}
                  </p>

                  <DelaysStatusGraph
                    class="mt-6"
                    :delays-by-status="delaysByStatus"
                    :delays-summary="delaysSummary"
                  />
                </div>

                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    {{ $t('HeadContract.DelaysSchedule.delayDaysByStatus') }}
                  </p>

                  <DelaysValueGraph
                    class="mt-6"
                    :delays-by-status="delaysByStatus"
                    :delays-summary="delaysSummary"
                    :total-delay-days="totalDelay"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="fetchDelaysAction.firstLoad"
            ref="delaysContainer"
            class="w-full overflow-y-auto relative"
          >
            <div class="flex flex-col w-full border border-gray-200 rounded-lg shadow-sm">
              <div
                class="px-2 py-4 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0"
                :class="{ 'z-30': increaseZIndex }"
              >
                <div class="space-y-4">
                  <!-- Filter Header -->
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <button
                        type="button"
                        class="group p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        @click="toggleExpand"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="delaysExpanded"
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                      </button>

                      <h3 class="text-lg font-semibold text-gray-900">
                        {{ $t('HeadContract.DelaysSchedule.delays') }}
                      </h3>

                      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                        <!-- Search Filter -->
                        <div class="space-y-2">
                          <div class="relative">
                            <AppInputIcon
                              v-model="searchForm.form.search"
                              type="text"
                              name="delays_filter"
                              class="form-input w-full"
                              :placeholder="$t('HeadContract.DelaysSchedule.placeholder.searchDelays')"
                            >
                              <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                              </template>
                            </AppInputIcon>
                          </div>
                        </div>

                        <!-- Status Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="searchForm.form.status"
                            name="status_filter"
                            class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="statusOptions"
                            :empty-text="$t('HeadContract.DelaysSchedule.placeholder.allStatuses')"
                          />
                        </div>

                        <!-- Assignee Filter -->
                        <div
                          v-if="!isReadOnly"
                          class="space-y-2"
                        >
                          <AppTypeAhead
                            v-model="nextActionUserName"
                            name="next_action_assignee_id"
                            class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :placeholder="$t('HeadContract.DelaysSchedule.placeholder.searchUsers')"
                            :suggestion-callback="getSuggestions"
                            :initial-suggestions="getSuggestions"
                            @value-selected="selectUser"
                          >
                            <template #icon>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
                            </template>
                          </AppTypeAhead>
                        </div>

                        <!-- Sort Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="sortColumn"
                            name="sort_filter"
                            class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="sortOptions"
                            :empty-text="$t('HeadContract.DelaysSchedule.placeholder.selectSortOption')"
                          />
                        </div>

                        <!-- Actions -->
                        <div class="space-y-2">
                          <button
                            type="button"
                            class="btn btn--secondary-blue w-full text-sm"
                            @click="resetSearch"
                          >
                            {{ $t('HeadContract.DelaysSchedule.button.clearFilters') }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <Transition
                      mode="out-in"
                      enter-active-class="animate__animated animate__fadeIn animate__faster"
                      leave-active-class="animate__animated animate__fadeOut animate__faster"
                    >
                      <p
                        v-if="filteredDelays.length > 0"
                        key="total-delays"
                        class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                      >
                        <span class="font-medium text-gray-900">{{ filteredDelays.length }}&nbsp;</span>

                        <span class="text-gray-500"> {{ $t('HeadContract.DelaysSchedule.delay', filteredDelays.length) }}</span>
                      </p>
                    </Transition>
                  </div>

                  <!-- Active Filters Display -->
                  <div
                    v-if="hasActiveFilters"
                    class="flex items-center space-x-2 pt-2"
                  >
                    <span class="text-sm text-gray-500">{{ $t('HeadContract.DelaysSchedule.activeFilters') }}:</span>

                    <div class="flex flex-wrap gap-2">
                      <span
                        v-if="searchForm.form.search"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {{ $t('HeadContract.DelaysSchedule.search') }}: "{{ searchForm.form.search }}"
                        <button
                          type="button"
                          class="ml-1.5 text-blue-600 hover:text-blue-800"
                          @click="searchForm.form.search = ''"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>

                      <span
                        v-if="!falsey(searchForm.form.status)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :style="{ color: DelayStatusTextColours[enumKeyFromValue(DelayStatus, searchForm.form.status)], backgroundColor: DelayStatusColours[enumKeyFromValue(DelayStatus, searchForm.form.status)] }"
                      >
                        {{ $t(`Models.Delay.statusTitles.${enumKeyFromValue(DelayStatus, searchForm.form.status)}`) }}
                        <button
                          type="button"
                          class="ml-1.5"
                          @click="searchForm.form.status = undefined"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>

                      <span
                        v-if="nextActionUserName"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-300 text-gray-700"
                      >
                        {{ nextActionUserName }}
                        <button
                          type="button"
                          class="ml-1.5 text-gray-600 hover:text-gray-800"
                          @click="clearUserFilter"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <table
                class="border-separate border-spacing-0 w-full z-10 text-sm"
              >
                <thead>
                  <tr class="child:px-2 child:py-3 z-10 sticky top-0 child:border-y child:border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                    <th class="text-left w-60">
                      <div class="flex items-center space-x-2">
                        <button
                          v-if="y > 60"
                          type="button"
                          @click="toggleExpand"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="delaysExpanded"
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                        </button>

                        <span>
                          {{ $t('HeadContract.DelaysSchedule.table.delay') }}
                        </span>
                      </div>
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.DelaysSchedule.table.dates') }}
                    </th>

                    <th class="text-left">
                      {{ project?.isContractor ? $t('HeadContract.DelaysSchedule.table.clientRef') : $t('HeadContract.DelaysSchedule.table.contractorRef') }}
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.DelaysSchedule.table.status') }}
                    </th>

                    <th class="text-right">
                      {{ $t('HeadContract.DelaysSchedule.table.days') }}
                    </th>

                    <th
                      v-if="projectsStore.project?.unarchived"
                      class="text-left w-60"
                    >
                      {{ $t('HeadContract.DelaysSchedule.table.nextAction') }}
                    </th>

                    <th
                      v-if="projectsStore.project?.unarchived && !isReadOnly"
                      class="text-left"
                    >
                      {{ $t('HeadContract.DelaysSchedule.table.assignee') }}
                    </th>

                    <th
                      v-if="projectsStore.project?.unarchived && !isReadOnly"
                      class="text-left"
                    >
                      {{ $t('HeadContract.DelaysSchedule.table.actions') }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="fetchDelaysAction.is(States.LOADING)">
                    <td
                      :colspan="delayTableColspan"
                      class="px-6 py-16 text-center"
                    >
                      <div class="flex flex-col items-center">
                        <AppSpinner class="h-8 w-8 mb-4" />

                        <h3 class="text-sm font-medium text-gray-900 mb-1">
                          {{ $t('HeadContract.DelaysSchedule.loadingDelays') }}
                        </h3>
                      </div>
                    </td>
                  </tr>

                  <template v-else-if="fetchDelaysAction.is(States.COMPLETE) && filteredDelays.length > 0">
                    <DelayRow
                      v-for="delay in filteredDelays"
                      :key="delay.id"
                      :delay="delay"
                      :read-only="isReadOnly"
                      @show-delay="showDelay(delay)"
                      @open-comment-modal="openCommentModal(delay)"
                      @accept-delay="acceptDelay(delay)"
                      @decline-delay="declineDelay(delay)"
                      @add-days-to-delay="addDaysToDelay(delay)"
                      @submit-delay="submitDelay(delay.id)"
                      @approve-delay="approveDelay(delay.id)"
                      @register-notice="registerNotice($event)"
                      @register-value-notice="registerValueNotice($event)"
                      @open-correspondence-modal="openCorrespondenceModal($event)"
                      @set-duration="setDelayDuration($event)"
                      @send-notice="sendNoticeForDelay($event)"
                      @save-next-action="updateNextActionDelay($event.delay, $event.next_action, $event.next_action_due_date, $event.next_action_assignee_id)"
                    />

                    <tr
                      v-if="!searchForm.form.search && fetchDelaysAction.is(States.COMPLETE)"
                      class="sticky bottom-0 bg-white border child:px-2 child:border-y first-child:border-l last-child:border-r font-semibold"
                    >
                      <td />

                      <td />

                      <td />

                      <td>
                        {{ $t('HeadContract.DelaysSchedule.table.total') }}
                      </td>

                      <td class="text-right">
                        {{ totalDelay }}
                      </td>

                      <td
                        v-if="projectsStore.project?.unarchived"
                      />

                      <td
                        v-if="projectsStore.project?.unarchived && !isReadOnly"
                      />

                      <td
                        v-if="projectsStore.project?.unarchived && !isReadOnly"
                      />
                    </tr>
                  </template>

                  <tr v-else-if="fetchDelaysAction.is(States.COMPLETE)">
                    <td
                      :colspan="delayTableColspan"
                      class="px-6 py-16 text-center"
                    >
                      <div class="flex flex-col items-center">
                        <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                        </div>

                        <h3 class="text-sm font-medium text-gray-900 mb-1">
                          {{ $t('HeadContract.DelaysSchedule.noDelaysFound') }}
                        </h3>

                        <p class="text-sm text-gray-500">
                          {{ $t('HeadContract.DelaysSchedule.tryAdjustingFilters') }}
                        </p>

                        <p
                          class="text-sm text-gray-500"
                        >
                          {{ $t('HeadContract.DelaysSchedule.createFirstDelay') }}
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <AppSpinner
            v-else
            class="mx-auto mt-5"
          />
        </template>

        <div
          v-else-if="fetchDelaysAction.is(States.LOADING)"
          class="flex w-full"
        >
          <AppSpinner class="mx-auto" />
        </div>

        <div
          v-else
          class="flex flex-col items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.DelaysSchedule.noDelaysFound') }}
          </h3>

          <p
            class="text-sm text-gray-500"
          >
            {{ $t('HeadContract.DelaysSchedule.createFirstDelay') }}
          </p>
        </div>
      </div>
    </div>

    <DelayShowModal
      v-if="showDelayModal"
      :delay-id="selectedDelay"
      @close="showDelayModal = false"
      @attach-variation="showDelayModal = false; attachVariation($event)"
    />

    <DelaySaveModal
      v-if="showCreateDelayModal"
      :project-id="$props.projectId"
      @close="showCreateDelayModal = false"
    />

    <DelayCommentModal
      v-if="showCommentModal"
      :project-id="props.projectId"
      :delay-id="selectedDelay"
      @close="showCommentModal = false"
    />

    <DelayAttachVariationModal
      v-if="showAttachModal"
      :project-id="props.projectId"
      :delay-id="selectedDelay"
      @close="showAttachModal = false"
    />

    <RegisterNoticeModal
      v-if="showRegisterModal"
      :delay-id="selectedDelay"
      @close="showRegisterModal = false"
    />

    <RegisterValueModal
      v-if="showValueRegisterModal"
      :delay-id="selectedDelay"
      @close="showValueRegisterModal = false"
      @provisioned="fetchSummary"
    />

    <DelayCorrespondenceModal
      v-if="showDelayCorrespondenceModal"
      :delay-id="selectedDelay"
      @close="showDelayCorrespondenceModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import DelayAttachVariationModal from '@/components/HeadContract/DelaysSchedule/DelayAttachVariationModal.vue';
import DelayCommentModal from '@/components/HeadContract/DelaysSchedule/DelayCommentModal.vue';
import DelayCorrespondenceModal from '@/components/HeadContract/DelaysSchedule/DelayCorrespondenceModal.vue';
import DelayRow from '@/components/HeadContract/DelaysSchedule/DelayRow.vue';
import DelaySaveModal from '@/components/HeadContract/DelaysSchedule/DelaySaveModal.vue';
import DelayShowModal from '@/components/HeadContract/DelaysSchedule/DelayShowModal.vue';
import DelaysStatusGraph from '@/components/HeadContract/DelaysSchedule/DelaysStatusGraph.vue';
import DelaysValueGraph from '@/components/HeadContract/DelaysSchedule/DelaysValueGraph.vue';
import RegisterNoticeModal from '@/components/HeadContract/DelaysSchedule/RegisterNoticeModal.vue';
import RegisterValueModal from '@/components/HeadContract/DelaysSchedule/RegisterValueModal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue, falsey, timeZone } from '@/helpers';
import Delay, { DelayStatus, DelayStatusColours, DelayStatusTextColours, DelayStatusTitles } from '@/models/Delay';
import { useDelaysStore } from '@/store/delays';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useDebounceFn, useScroll } from '@vueuse/core';
import { useCollect } from 'pinia-orm/dist/helpers';
import Swal from 'sweetalert2';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  delayId?: string;
}>();

const delaysStore = useDelaysStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const isReadOnly = userStore.isClientManager();

const project = computed(() => {
  return projectsStore.project;
});

const delayTableColspan = computed(() => {
  if(!projectsStore.project?.unarchived) {
    return 5;
  }

  if(isReadOnly) {
    return 6;
  }

  return 8;
});

const delaysSummary = ref<DelaysSummary>({
  approved_delays_total: 0,
  unapproved_delays_total: 0,
  registered: 0,
  submitted: 0,
  provisioned: 0,
  approved: 0,
  total_delays: 0,
  completed_delays_total: 0,
});

const fetchDelaysAction = useStoreApiAction(delaysStore.fetchDelays);
const fetchDelaysSummaryAction = useStoreApiAction(delaysStore.fetchDelaysSummary);

const fetchSummary = () => {
  fetchDelaysSummaryAction.request(project.value.id).then((summary) => {
    delaysSummary.value = summary;
  }).catch(console.error);
};

const router = useRouter();

watch(project, (newValue) => {
  if(newValue && !newValue.boq_locked) {
    router.push({ name: 'BillOfQuantities', params: { projectId: newValue.id } });
  }
}, { immediate: true });

const delays = computed(() => {
  return delaysStore.models
    .where('project_id', parseInt(props.projectId))
    .orderBy(
      'status',
    ).get();
});

const sortColumn = ref<'status' | 'code' | 'date_of_occurrence' | 'created_at' | 'amount' | 'next_action'>('status');

const sortOptions = computed(() => {
  const options = [
    {
      value: 'status',
      label: 'Sort by status',
    },
    // {
    //   value: 'code',
    //   label: 'Code',
    // },
    {
      value: 'date_of_occurrence',
      label: 'Sort by date of occurrence',
    },
    {
      value: 'created_at',
      label: 'Sort by latest',
    },
    {
      value: 'amount',
      label: 'Sort by amount',
    },
    {
      value: 'next_action',
      label: 'Sort by next action due date',
    },
  ];

  return options;
});

const sortedDelays = computed(() => {
  let sortedDelays = [];

  switch (sortColumn.value) {
    case 'status':
      sortedDelays = useCollect(delays.value).sortBy([[sortColumn.value, 'asc'], ['created_at', 'desc']]);
      break;

    case 'date_of_occurrence':

      // fall-through
    case 'created_at':
      sortedDelays = useCollect(delays.value).sortBy([[sortColumn.value, 'desc']]);
      break;

    case 'amount':
      sortedDelays = useCollect(delays.value).sortBy((model) => model.days ? -model.days : 0);

      break;

    case 'code':
      sortedDelays = delaysStore.models.where('project_id', parseInt(props.projectId)).get()
        .sort((a, b) => {
          if(!falsey(a.code) && !falsey(b.code)) {
            return a.code.localeCompare(b.code);
          } else if(falsey(a.code) && !falsey(b.code)) {
            return 1;
          }

          return -1;
        });

      break;

    case 'next_action':
      sortedDelays = delaysStore.models.where('project_id', parseInt(props.projectId)).get()
        .sort((a, b) => {
          if(!falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
            if(a.nextActionDueIn < 0) {
              return 1;
            }

            if(b.nextActionDueIn < 0) {
              return -1;
            }

            return a.nextActionDueIn - b.nextActionDueIn;
          } else if(falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
            return 1;
          }

          return -1;
        });

      break;
  }

  return sortedDelays;
});

const totalDelay = computed(() => {
  return delaysSummary.value.unapproved_delays_total + delaysSummary.value.approved_delays_total +
    delaysSummary.value.completed_delays_total;
});

const delaysByStatus = computed(() => {
  return delays.value.reduce((delaysMap, delay) => {
    const key = enumKeyFromValue(DelayStatus, delay.status);

    if(key === 'declined') {
      return delaysMap;
    }

    if(delaysMap.has(key)) {
      const delays = delaysMap.get(key);

      delays.push(delay);
      delaysMap.set(key, delays);
    } else {
      delaysMap.set(key, [delay]);
    }

    return delaysMap;
  }, new Map<string, Delay[]>());
});

const acceptDelayAction = useStoreApiAction(delaysStore.acceptDelay);
const declineDelayAction = useStoreApiAction(delaysStore.declineDelay);
const submitDelayAction = useStoreApiAction(delaysStore.submitDelay);
const approveDelayAction = useStoreApiAction(delaysStore.approveDelay);
const updateDelayAction = useStoreApiAction(delaysStore.updateDelay);
const sendNoticeForDelayAction = useStoreApiAction(delaysStore.sendNoticeForDelay);
const updateNextActionDelayAction = useStoreApiAction(delaysStore.updateNextActionDelay);

const updateNextActionDelay = (
  delay: Delay,
  next_action: string,
  next_action_due_date: string,
  next_action_assignee_id: number,
) => {
  updateNextActionDelayAction.request(delay.id, {
    next_action,
    next_action_due_date,
    next_action_assignee_id,
  }).catch(console.error);
};

const acceptDelay = (delay: Delay) => {
  Swal.fire({
    title: 'Accept Delay?',
    text: delay.description,
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      acceptDelayAction.request(delay.id).then(() => {
        console.log('accepted');
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const declineDelay = (delay: Delay) => {
  Swal.fire({
    title: 'Decline Delay?',
    text: delay.description,
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      declineDelayAction.request(delay.id).then(() => {
        console.log('declined');
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const submitDelay = (id: Id) => {
  Swal.fire({
    title: 'Submit Delay?',
    text: 'Submit delay to be approved by client',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.delay_submission_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      submitDelayAction.request(id, { description: result.value, time_zone: timeZone }).then((data) => {
        Swal.fire('Delay Submitted', '', 'success');
      }).catch((error) => {
        Swal.fire('Failed to Submit', error?.data?.message ?? 'Failed to submit delay', 'error');
      });
    }
  });
};

const approveDelay = (id: Id) => {
  Swal.fire({
    title: 'Approve Delay?',
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      approveDelayAction.request(id, { time_zone: timeZone }).then((data) => {
        console.log(data);
      }).catch((error) => {
        Swal.fire('Failed to Approve', '', 'error');
      });
    }
  });
};

const sendNoticeForDelay = (delay: Delay) => {
  Swal.fire({
    title: 'Send Notice for Delay?',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.nod_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      sendNoticeForDelayAction.request(delay.id, { description: result.value, time_zone: timeZone }).then((data) => {
        Swal.fire('Notice Sent', '', 'success');
      }).catch((error) => {
        Swal.fire('Failed to Send Notice', error?.data?.message, 'error');
      });
    }
  });
};

const selectedDelay = ref();
const showModal = ref(false);
const showCreateDelayModal = ref(false);

const searchForm = useFormData({
  search: '',
  status: undefined,
  next_action_assignee_id: undefined,
});

const nextActionUserName = ref('');
const filteredDelayIds = ref<number[] | undefined>([]);

const statusOptions = computed(() => {
  const options = [];

  for(const key in DelayStatus) {
    options.push({ value: DelayStatus[key], label: DelayStatusTitles[key] });
  }

  return options;
});

const searchDelays = () => {
  fetchDelaysAction.request(project.value?.id, searchForm.form).then(({ data: delays }) => {
    filteredDelayIds.value = delays.map((delay) => delay.id);
  }).catch((error) => {
    console.log(error);
  });
};

const filteredDelays = computed(() => {
  if(!searchForm.isDirty.value) {
    return sortedDelays.value;
  }

  return sortedDelays.value.filter((delay) => {
    return filteredDelayIds.value.includes(delay.id);
  });
});

const debounceSearch = useDebounceFn(searchDelays, 300);
const filterActive = ref(false);

watch(searchForm.form, (newValue) => {
  if(!newValue.search && newValue.status === undefined) {
    filterActive.value = false;
  } else {
    filterActive.value = true;
  }
});

watch(searchForm.form, () => {
  if(filterActive.value) {
    debounceSearch();
  }
});

const resetSearch = () => {
  filterActive.value = false;

  searchForm.resetData();

  searchDelays();
};

const showDelayModal = ref(false);

const showDelay = (delay: Delay) => {
  selectedDelay.value = delay.id;

  showDelayModal.value = true;
};

// const totalORders = ref(0);
// const ordersIndex = ref(0);
// const { wrapperProps } = useVirtualList(totalORders, ordersIndex, 40);
// const orderListRef = ref();
// const page = ref(1);
// const paginator = ref<ResourcePaginator<VariationOrder>>();

// const recordQueue = useQueue<number>(10);

// const initialFetch = () => {
//   variationOrdersStore.models.query().where('plant_id', props.plant.id).delete();

//   fetchVariationsAction.request(props.plant.id, plantsStore.filter.form).then((newPaginator) => {
//     recordQueue.reset();
//     totalRecords.value = 0;
//     totalRecords.value += newPaginator.data.length;

//     recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//     recordQueue.enqueueFront(newPaginator.data.map((record: PlantDailyRecord) => {
//       return record.id;
//     }));

//     paginator.value = newPaginator;
//   });
// };

// const scrolled = () => {
//   const recordList = recordListRef.value;

//   if(recordList) {
//     const scrollable = recordList.scrollHeight > recordList.clientHeight;

//     if(
//       page.value > 1 &&
//       scrollable &&
//       recordList.scrollTop === 0
//     ) {
//       fetchRecordsAction.request(props.plant.id, { page: page.value - 1, ...plantsStore.filter.form })
//         .then((newPaginator) => {
//           paginator.value = newPaginator;

//           totalRecords.value -= newPaginator.data.length;
//           recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//           const recordsToDelete = recordQueue.enqueueBack(newPaginator.data.map((record: PlantDailyRecord) => {
//             return record.id;
//           }));

//           plantDailyRecordsStore.models.query().whereId(recordsToDelete).delete();

//           recordList.scrollTop = Math.round(recordList.scrollHeight * 0.1);
//           page.value = newPaginator.meta.current_page;
//         });
//     } else if(
//       paginator.value.meta.last_page !== page.value &&
//       scrollable &&
//       recordList.scrollHeight - recordList.scrollTop === recordList.clientHeight
//     ) {
//       fetchRecordsAction.request(props.plant.id, { page: page.value + 1, ...plantsStore.filter.form })
//         .then((newPaginator) => {
//           paginator.value = newPaginator;

//           totalRecords.value += newPaginator.data.length;
//           recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//           const recordsToDelete = recordQueue.enqueueFront(newPaginator.data.map((record: PlantDailyRecord) => {
//             return record.id;
//           }));

//           plantDailyRecordsStore.models.query().whereId(recordsToDelete).delete();

//           recordList.scrollTop = Math.round(recordList.scrollHeight * 0.2);
//           page.value = newPaginator.meta.current_page;
//         });
//     }
//   }
// };

const addDaysToDelay = (delay: Delay) => {
  Swal.fire({
    icon: 'info',
    title: 'Add Days to Delay',
    text: 'Delay Duration (Days)',
    input: 'number',
    showCancelButton: true,
  }).then((result) => {
    console.log(result);

    if(result.isConfirmed && result.value) {
      updateDelayAction.request(delay.id, { days: result.value }).then((delay) => {
        fetchDelaysSummaryAction.request(project.value.id).then((summary) => {
          delaysSummary.value = summary;
        });
      }).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Failed to add days',
        });
      });
    }
  });
};

const showCommentModal = ref(false);

const openCommentModal = (delay: Delay) => {
  selectedDelay.value = delay.id;

  showCommentModal.value = true;
};

const delaysExpanded = ref(false);

const toggleExpand = () => {
  if(delaysExpanded.value) {
    delaysExpanded.value = false;
  } else {
    delaysExpanded.value = true;
  }
};

const showAttachModal = ref(false);

const attachVariation = (id: Id) => {
  selectedDelay.value = id;
  showAttachModal.value = true;
};

const showDelayCorrespondenceModal = ref(false);

const openCorrespondenceModal = (delay: Delay) => {
  selectedDelay.value = delay.id;

  showDelayCorrespondenceModal.value = true;
};

const showRegisterModal = ref(false);

const registerNotice = (delay: Delay) => {
  selectedDelay.value = delay.id;

  showRegisterModal.value = true;
};

const showValueRegisterModal = ref(false);

const registerValueNotice = (delay: Delay) => {
  selectedDelay.value = delay.id;

  showValueRegisterModal.value = true;
};

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    fetchDelaysAction.request(newValue).catch((error) => {
      console.log(error);
    });

    fetchDelaysSummaryAction.request(newValue).then((summary) => {
      delaysSummary.value = summary;
    }).catch(console.error);

    if(props.delayId && !isReadOnly) {
      selectedDelay.value = parseInt(props.delayId);
      showDelayModal.value = true;
    }
  }
}, { immediate: true });

const setDelayDurationAction = useStoreApiAction(delaysStore.updateDelay);

const setDelayDuration = (data: { delay: Delay; duration: number; start_date?: string; end_date?: string }) => {
  const updateData: { days: number; start_date?: string; end_date?: string } = { days: data.duration };

  if(data.start_date) {
    updateData.start_date = data.start_date;
  }

  if(data.end_date) {
    updateData.end_date = data.end_date;
  }

  setDelayDurationAction.request(data.delay.id, updateData).then().catch((error) => {
    console.log(error);
  });
};

const suggestedUsers = ref<{ id: number; name: string }[]>([]);

const getSuggestions = async () => {
  suggestedUsers.value = await projectsStore
    .searchUsers(project.value?.id, { name: nextActionUserName.value });

  return suggestedUsers.value.map((value) => value.name);
};

const selectUser = (index: number) => {
  nextTick(() => {
    if(suggestedUsers.value[index]) {
      searchForm.form.next_action_assignee_id = suggestedUsers.value[index].id;
      searchDelays();
    }
  });
};

const clearUserFilter = () => {
  nextActionUserName.value = '';
  searchForm.form.next_action_assignee_id = undefined;
  searchDelays();
};

const hasActiveFilters = computed(() => {
  return searchForm.isDirty.value || delaysExpanded.value;
});

const delaysContainer = useTemplateRef('delaysContainer');
const { y } = useScroll(delaysContainer);
const increaseZIndex = ref(true);

watch(y, (newValue) => {
  if(newValue > 0) {
    increaseZIndex.value = false;
  } else {
    increaseZIndex.value = true;
  }
});
</script>

<style scoped></style>
